import type { WorkboardEmailTemplate } from 'api/models/WorkboardEmailTemplate';
import { preferenceKeyForUser } from 'common/constants/userPreferences';
import type { EmailTemplatePresetBase } from 'dashboard409a/hooks/useEmailTemplatePresets';
import {
  createWorkboardEmailTemplate,
  deleteWorkboardEmailTemplate,
  listWorkboardEmailTemplates,
  updateWorkboardEmailTemplate,
  type WorkboardEmailTemplateKind,
} from 'dashboard409a/services/workboardEmailTemplates';

export type StoredEmailTemplatePreset<T extends EmailTemplatePresetBase> = {
  id: number;
  name: string;
  template: T;
};

export const getWorkboardEmailTemplatesQueryKey = (
  userId: number | undefined,
  kind: WorkboardEmailTemplateKind
) => ['workboard-email-templates', userId, kind] as const;

export const fromWorkboardEmailTemplate = <T extends EmailTemplatePresetBase>(
  row: WorkboardEmailTemplate,
  toTemplate: (row: WorkboardEmailTemplate) => T
): StoredEmailTemplatePreset<T> => ({
    id: row.id as number,
    name: row.name,
    template: toTemplate(row),
  });

export const toWorkboardEmailTemplatePayload = <T extends EmailTemplatePresetBase>(
  name: string,
  template: T
): Pick<WorkboardEmailTemplate, 'name' | 'email_subject' | 'introduction'> => ({
    name,
    email_subject: template.emailSubject,
    introduction: template.introduction,
  });

export const migrateLocalEmailTemplatePresetsIfNeeded = async ({
  userId,
  kind,
  presetsKey,
  migrationKey,
}: {
  userId: number;
  kind: WorkboardEmailTemplateKind;
  presetsKey: string;
  migrationKey: string;
}): Promise<void> => {
  const { getPreference, migrateLegacyLocalStorageKey, removePreference, setPreference } = await import(
    'services/storage/preferencesIndexedDB'
  );

  const userMigrationKey = preferenceKeyForUser(migrationKey, userId);
  const alreadyMigrated = await getPreference<boolean>(userMigrationKey);
  if (alreadyMigrated) {
    return;
  }

  const serverResponse = await listWorkboardEmailTemplates(kind);
  if ((serverResponse.definitions ?? []).length > 0) {
    await setPreference(userMigrationKey, true);
    return;
  }

  await migrateLegacyLocalStorageKey(presetsKey);
  const localPresets = await getPreference<
    Array<{ name: string; template: EmailTemplatePresetBase }>
  >(presetsKey);

  if (!localPresets?.length) {
    await setPreference(userMigrationKey, true);
    return;
  }

  const results = await Promise.allSettled(
    localPresets.map(preset =>
      createWorkboardEmailTemplate(kind, toWorkboardEmailTemplatePayload(preset.name, preset.template))
    )
  );

  const allSucceeded = results.every(result => result.status === 'fulfilled');
  if (allSucceeded) {
    await removePreference(presetsKey);
    await setPreference(userMigrationKey, true);
  }
};

export const saveWorkboardEmailTemplatePreset = async <T extends EmailTemplatePresetBase>({
  kind,
  existingId,
  name,
  template,
}: {
  kind: WorkboardEmailTemplateKind;
  existingId?: number;
  name: string;
    template: T;
}): Promise<WorkboardEmailTemplate> => {
  const payload = toWorkboardEmailTemplatePayload(name, template);
  if (existingId) {
    return updateWorkboardEmailTemplate(String(existingId), payload);
  }
  return createWorkboardEmailTemplate(kind, payload);
};

export const deleteWorkboardEmailTemplatePreset = async (id: number): Promise<void> =>
  deleteWorkboardEmailTemplate(String(id));
